<template>
  <div class="custom-tabs-container">
    <Tabs v-model:value="activeTab">
      <TabList>
        <Tab
          v-for="tab in tabs"
          :key="tab.value"
          :value="tab.value"
          :class="tab.style"
          class="dark:hover:text-text-primary-dark! hover:text-text-secondary! hover:font-bold! bg-transparent"
          @click="handleTabClick(tab.value)"
        >
          <span
            class="text-text-secondary! dark:text-text-primary-dark! tab-content"
          >
            {{ tab.content }}
          </span>
          <font-awesome-icon
            class="text-text-secondary! ml-2 dark:text-text-primary-dark!"
            v-if="tab.icon"
            :icon="tab.icon"
          />
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel v-for="tab in tabs" :key="tab.content" :value="tab.value">
          <slot :name="tab.value" :tab="tab"> </slot>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";

// Definir la interfaz para los tabs
interface TabItem {
  title: string;
  value: string;
  content: string;
  style?: string;
  icon?: string;
}

const props = defineProps<{
  tabs: TabItem[];
  modelValue?: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: string];
  tabChange: [value: string];
}>();

const activeTab = ref(props.modelValue || "0");

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      activeTab.value = newValue;
    }
  }
);

// Función para manejar el click en el tab
const handleTabClick = (tabValue: string) => {
  activeTab.value = tabValue;
  emit("update:modelValue", tabValue);
  emit("tabChange", tabValue);
};
</script>

<style scoped>
.custom-tabs-container,
.custom-tabs-container *,
.custom-tabs-container *::before,
.custom-tabs-container *::after {
  border: 0 !important;
  border-style: none !important;
  border-width: 0 !important;
  border-color: transparent !important;
  outline: none !important;
  box-shadow: none !important;
}

/* Contenedor principal */
.custom-tabs-container {
  background: transparent;
}

/* Componentes PrimeVue - eliminar todos los bordes */
.custom-tabs-container :deep(.p-tabs),
.custom-tabs-container :deep(.p-tablist),
.custom-tabs-container :deep(.p-tabpanels),
.custom-tabs-container :deep(.p-tab),
.custom-tabs-container :deep(.p-tabpanel) {
  border: 0 !important;
  border-style: none !important;
  border-width: 0 !important;
  border-color: transparent !important;
  outline: none !important;
  box-shadow: none !important;
  background-color: transparent !important;
}

/* Estados de los tabs - eliminar bordes */
.custom-tabs-container :deep(.p-tab:hover),
.custom-tabs-container :deep(.p-tab:focus),
.custom-tabs-container :deep(.p-tab:active) {
  border: 0 !important;
  border-style: none !important;
  border-width: 0 !important;
  border-color: transparent !important;
  outline: none !important;
  box-shadow: none !important;
}

/* Tab activo - agregar el borde inferior rojo Y font-weight bold */
/* .custom-tabs-container :deep(.p-tab.p-tab-active) {
  border: 0 !important;
  border-style: none !important;
  border-width: 0 !important;
  border-color: transparent !important;
  outline: none !important;
  box-shadow: none !important;
  border-bottom: 2px solid #d74028 !important;
} */
.custom-tabs-container :deep(.p-tab.p-tab-active .tab-content) {
  font-weight: bold !important;
}
/* Aplicar font-weight bold solo al contenido del tab activo */

/* Agregar separación a todos los tabs */
.custom-tabs-container :deep(.p-tab .tab-content) {
  padding-bottom: 8px !important;
}

/* Tab activo en hover - mantener solo el borde rojo */
/* .custom-tabs-container :deep(.p-tab.p-tab-active:hover),
.custom-tabs-container :deep(.p-tab.p-tab-active:focus) {
  border: 0 !important;
  border-style: none !important;
  border-width: 0 !important;
  border-color: transparent !important;
  outline: none !important;
  box-shadow: none !important;
} */

/* Sobrescribir cualquier estilo global que pueda estar aplicando bordes */
.custom-tabs-container :deep(.p-tabs .p-tablist .p-tab) {
  border-top: 0 !important;
  border-left: 0 !important;
  border-right: 0 !important;
  border-bottom: 0 !important;
}

.custom-tabs-container :deep(.p-tabs .p-tablist .p-tab.p-tab-active) {
  border-top: 0 !important;
  border-left: 0 !important;
  border-right: 0 !important;
  border-bottom: 1px solid #d74028 !important;
}
</style>
